import {
  cancelMatterSurvey,
  createMatterSurvey,
  deleteMatterSurvey,
  deliverMatterSurvey,
  listMatterSurveys,
  reopenMatterSurvey,
  retrieveMatterSurvey,
  retrieveMatterSurveyAnswers,
  retrieveMatterSurveyAnswerSession,
  retrieveMatterSurveyQuestions,
  saveMatterSurveyDraft,
  submitMatterSurveyOnBehalf,
} from "@/lib/data/requests/matters/matter-survey";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const matterSurveysQueryOptions = (matterId: string) =>
  queryOptions({
    queryKey: [queryKeys.matterSurveys, matterId],
    queryFn: () => listMatterSurveys(matterId),
    enabled: Boolean(matterId),
  });

export const useMatterSurveys = (matterId: string) =>
  useQuery(matterSurveysQueryOptions(matterId));

export const matterSurveyQueryOptions = (
  surveyId: string,
  matterId: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: [queryKeys.matterSurvey, surveyId, "v4", matterId],
    queryFn: () => retrieveMatterSurvey(surveyId, matterId),
    enabled: enabled && Boolean(surveyId) && Boolean(matterId),
  });

export const useMatterSurvey = (
  surveyId: string,
  matterId: string,
  enabled = true,
) => useQuery(matterSurveyQueryOptions(surveyId, matterId, enabled));

export const matterSurveyQuestionsQueryOptions = (
  surveyId: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: [queryKeys.matterSurveyQuestions, surveyId],
    queryFn: () => retrieveMatterSurveyQuestions(surveyId),
    enabled: enabled && Boolean(surveyId),
  });

export const useMatterSurveyQuestions = (surveyId: string, enabled = true) =>
  useQuery(matterSurveyQuestionsQueryOptions(surveyId, enabled));

export const useMatterSurveyAnswers = (surveyId: string, enabled = true) =>
  useQuery({
    queryKey: [queryKeys.matterSurvey, surveyId, "answers"],
    queryFn: () => retrieveMatterSurveyAnswers(surveyId),
    enabled: enabled && Boolean(surveyId),
  });

export const useMatterSurveyAnswerSession = (
  surveyId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: [queryKeys.matterSurvey, surveyId, "answer-session"],
    queryFn: () => retrieveMatterSurveyAnswerSession(surveyId),
    enabled: enabled && Boolean(surveyId),
    gcTime: 0,
  });

export const useSaveMatterSurveyDraft = () =>
  useMutation({ mutationFn: saveMatterSurveyDraft });

export const useSubmitMatterSurveyOnBehalf = (matterId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitMatterSurveyOnBehalf,
    onSuccess: async (_result, { surveyId }) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSurvey, surveyId],
        refetchType: "none",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSurveys, matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: matterSurveyQueryOptions(surveyId, matterId).queryKey,
          exact: true,
        }),
      ]);
    },
  });
};

export const useReopenMatterSurvey = (matterId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reopenMatterSurvey,
    onSuccess: async (_result, { surveyId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSurveys, matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSurvey, surveyId],
        }),
      ]);
    },
  });
};

export const useCreateMatterSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMatterSurvey,
    onSuccess: async (_result, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSurveys, payload.matterId],
      });
    },
  });
};

export const useCancelMatterSurvey = (matterId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelMatterSurvey,
    onSuccess: async (_result, surveyId) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSurveys, matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSurvey, surveyId],
        }),
      ]);
    },
  });
};

export const useDeleteMatterSurvey = (matterId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMatterSurvey,
    onSuccess: async (_result, surveyId) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSurveys, matterId],
      });
      queryClient.removeQueries({
        queryKey: [queryKeys.matterSurvey, surveyId],
      });
      queryClient.removeQueries({
        queryKey: [queryKeys.matterSurveyQuestions, surveyId],
      });
    },
  });
};

export const useDeliverMatterSurvey = () =>
  useMutation({ mutationFn: deliverMatterSurvey });
